import {
    PRODUCT_BACKORDER,
    PRODUCT_PAGE_RELATIONS,
    PRODUCT_PREORDER,
    STORM_PACKAGE_TYPES,
} from "theme/config/constants";
import React, { Component } from "react";
import { getState, injectModels } from "core-web/state";

import Cookies from "js-cookie";
import GetProductPriceQtyBreak from "core-web/libs/GrebbCommerceAPI/Products/GetProductPriceQtyBreak";
import GetProductRelations from "core-web/libs/GrebbCommerceAPI/Products/GetProductRelations";
import Meta from "core-web/components/Meta";
import PropTypes from "prop-types";
import { buildQueryString } from "core-web/libs/GrebbCommerceAPI/util";
import comparePriceToParent from "core-web/utils/comparePriceToParent";
import get from "core-web/utils/get";
import getQueryParams from "core-web/utils/getQueryParams";
import hideOverlays from "core-web/state/models/Overlay/dispatches/hideOverlays";
import { inServer } from "core-web/constants";
import { isEmpty } from "core-web/utils/object";
import { onHandCheck } from "core-web/utils/getProductStockStatus";
import { withRouter } from "react-router-dom";

const initialState = {
    isPackage: false,
    product: null,
    qtyBreaks: [],
    quantity: 1,
    productRelations: [],
    variantRelations: {},
    relationsByCode: {},
    relationsFetched: false,
    selectedProduct: null,
    selectedProductVariants: {},
    bestHistoricalPrice: {},
};

export const getFlag = (code, product = {}) => {
    const findFlag = (flag) => {
        if (Array.isArray(code)) {
            return code.some((c) => c.toLowerCase() === flag.code?.toLowerCase());
        }

        return flag.code?.toLowerCase() === code?.toLowerCase();
    };
    return get(product, "format.flags", []).find(findFlag) || false;
};

export const getParametric = (code, product = {}) => {
    const parametrics = product.parametrics || [];
    const variantParametrics = product.variant_parametrics || [];

    const findParametric = (parametric) => {
        if (Array.isArray(code)) {
            return code.indexOf(parametric.code) !== -1 && parametric?.value?.length > 0;
        }
        return parametric.value?.length > 0 && parametric.code === code;
    };

    return parametrics.find(findParametric) || variantParametrics.find(findParametric);
};

export const isVariantProduct = (product) => (product.variants || []).length > 0;
export const isPackageProduct = (product) => STORM_PACKAGE_TYPES.indexOf(product.type) !== -1;

export const handleAllVariants = (variants, googleDiscount, specialPrice) => {
    if (variants?.length) {
        return variants.map((variant) => {
            const clonedVariant = Object.assign({}, variant);
            if (googleDiscount && googleDiscount.productPartNo === variant.part_no) {
                const googleDiscountPriceExVat = googleDiscount.discountPriceIncVat / variant.vat_rate;
                if (variant.price > googleDiscountPriceExVat) {
                    clonedVariant.googleDiscountExpirationDate = googleDiscount.discountExpirationDate;
                    clonedVariant.googleDiscountPrice = googleDiscountPriceExVat;
                    clonedVariant.googleDiscountTimeStamp = googleDiscount.discountTimeStamp;
                }
            } else if (specialPrice && specialPrice.productPartNo === variant.part_no) {
                const specialPriceExVat = specialPrice.discountPriceIncVat / variant.vat_rate;
                if (variant.price > specialPriceExVat) {
                    clonedVariant.specialPriceExpirationDate = specialPrice.discountExpirationDate;
                    clonedVariant.specialPrice = specialPriceExVat;
                    clonedVariant.specialPriceTimeStamp = specialPrice.discountTimeStamp;
                }
            }

            return clonedVariant;
        });
    } else {
        return variants;
    }
};

class ProductPageContainer extends Component {
    static propTypes = {
        loading: PropTypes.func,
        location: PropTypes.object.isRequired,
        page: PropTypes.object.isRequired,
        render: PropTypes.func.isRequired,
        shouldFetchRelations: PropTypes.bool,
    };

    static defaultProps = {
        loading: null,
        shouldFetchRelations: true,
    };

    state = this.resetState(this.props?.page?.data?.product);

    componentDidMount() {
        this.getProductRelations();
        this.getQtyBreaks();

        const { page } = this.props;

        if (page.data?.automatedDiscountData) {
            const { provider, productPartNo, discountTimeStamp } = page.data.automatedDiscountData;

            if (provider !== "google" && provider !== "api") {
                return;
            }

            const prefix = provider === "google" ? "googleDiscount" : "specialPrice";
            const cookieName = `${prefix}-${productPartNo}`;

            if (!Cookies.get(cookieName)) {
                Cookies.set(cookieName, JSON.stringify(page.data.automatedDiscountData), {
                    expires: discountTimeStamp,
                });
            }
        }
    }

    shouldComponentUpdate(nextProps, nextState) {
        const product = get(nextProps, "page.data.product", null);
        if (JSON.stringify(nextState) !== JSON.stringify(this.state)) {
            return true;
        }

        const productVariants = product?.variants || [];
        if (
            ((product || {}).part_no !== (this.state.product || {}).part_no &&
                (!productVariants.length || productVariants[0].part_no !== (this.state.product || {}).part_no)) ||
            ((product || {}).price !== (this.state.product || {}).price &&
                (!productVariants.length || productVariants[0].price !== (this.state.product || {}).price)) ||
            ((product || {}).price_list_id !== (this.state.product || {}).price_list_id &&
                (!productVariants.length ||
                    productVariants[0].price_list_id !== (this.state.product || {}).price_list_id)) ||
            JSON.stringify(nextProps.page.data.automatedDiscountData ?? {}) !==
                JSON.stringify(this.props.page.data.automatedDiscountData ?? {})
        ) {
            this.setState(this.resetState(product), () => {
                this.getProductRelations();
                this.getQtyBreaks();
            });
        }

        return false;
    }

    componentWillUnmount() {
        const { current } = getState("overlay");
        if (current === "gallery_zoom_overlay") {
            hideOverlays();
        }
    }

    resetState(product) {
        const { page } = this.props;
        const newState = { ...initialState };
        const search = page.search;
        const query = getQueryParams({ search });
        if (!product) {
            return initialState;
        }

        const hasPartNo = product.variants.some((p) => p.part_no === query.p);

        const presetPartNoLocationState = this.props.location.state?.presetPartNo;
        const hasPresetPartNo = product.variants.some((p) => p.part_no === presetPartNoLocationState);

        const variants = handleAllVariants(product.variants, null, null);

        // Remove queryParam from URL if query.id doesn't exists in product.variants
        if (!hasPartNo && !inServer) {
            const location = window.location;
            let queryParams = "";
            if (!isEmpty(query)) {
                if (query.hasOwnProperty("p")) {
                    delete query["p"];
                }
                queryParams = `?${buildQueryString(query)}`;
            }

            if (hasPresetPartNo && !query.p) {
                query.p = presetPartNoLocationState;

                queryParams = `?${buildQueryString(query)}`;
            }

            const currentPathAndSearch = `${location.pathname}${location.search}`;
            const newPathAndSearch = `${location.pathname}${queryParams}`;
            if (currentPathAndSearch !== newPathAndSearch) {
                window.history.replaceState(null, null, `${location.pathname}${queryParams}`);
            }
        }

        let isVariant = isVariantProduct(product);
        const isPackage = isPackageProduct(product);

        if (isVariant && product.variants.length === 1) {
            const firstVariant = product.variants[0];
            product = {
                ...product,
                ...firstVariant,
                files: [...product.files, ...firstVariant.files],
                name: firstVariant.variant_name || product.name,
                unique_name: product.unique_name,
                manufacturer: product.manufacturer,
                variants: [],
                parametrics: [...(product.parametrics || []), ...(firstVariant.parametrics || [])],
                variant_parametrics: [
                    ...(product.variant_parametrics || []),
                    ...(firstVariant.variant_parametrics || []),
                ],
                format: {
                    flags: [...(product.format.flags || []), ...(firstVariant.format.flags || [])],
                    image: firstVariant.format.image || product.format.image || "",
                },
            };
            isVariant = false;
        }

        const selectedProductVariants = {};
        if (isPackage && !isVariant && (product.included_products || []).length) {
            product.included_products.forEach((subProduct) => {
                if ((subProduct.variants || []).length > 1) {
                    selectedProductVariants[subProduct.id] = null;
                } else {
                    selectedProductVariants[subProduct.id] = JSON.parse(JSON.stringify(subProduct));
                }
            });
        } else if (isVariant && !hasPartNo && !hasPresetPartNo) {
            selectedProductVariants[product.id] = null;
        } else if (isVariant && (hasPartNo || hasPresetPartNo)) {
            const selectedVariant = variants.find((p) => p.part_no === product.part_no);
            selectedProductVariants[product.id] = JSON.parse(JSON.stringify(selectedVariant));
        } else {
            selectedProductVariants[product.id] = product;
        }

        const selectedVariant = selectedProductVariants[product.id] || {};

        const { best_historical_price_inc_vat: bestHistoricalPriceIncVat } = product;
        const bestHistoricalPrice = bestHistoricalPriceIncVat / product.vat_rate;

        const automatedDiscountData = page.data?.automatedDiscountData ?? {};
        const selectedProduct = this.getSelectedProductData(selectedVariant, product, variants, automatedDiscountData);

        if (page?.data?.automatedDiscountData) {
            this.triggerDiscountTimer(
                page.data.automatedDiscountData,
                variants,
                page.data.automatedDiscountData.provider,
            );
        }

        return {
            ...newState,
            bestHistoricalPrice,
            isPackage,
            product,
            quantity: product.recommended_quantity ? product.recommended_quantity : 1,
            selectedProduct,
            selectedProductVariants,
            variants,
        };
    }

    triggerDiscountTimer(discount, variants, type) {
        const { discountTimeStamp } = discount;
        const currentTime = Math.floor(new Date().getTime() / 1000);
        const discountRemainingTime = discountTimeStamp - currentTime;
        const isGAD = type === "google";

        setTimeout(() => {
            const cleanupVariants = variants.map((variant) => {
                const clonedVariant = Object.assign({}, variant);

                if (isGAD) {
                    delete clonedVariant.googleDiscountExpirationDate;
                    delete clonedVariant.googleDiscountPrice;
                    delete clonedVariant.discountTimeStamp;
                } else {
                    delete clonedVariant.specialPriceExpirationDate;
                    delete clonedVariant.specialPrice;
                    delete clonedVariant.discountTimeStamp;
                }
                return clonedVariant;
            });

            const currentSelectedProduct = this.state.selectedProduct;
            const clonedCurrentSelectedProduct = Object.assign({}, currentSelectedProduct);

            if (currentSelectedProduct.googleDiscountPrice) {
                delete clonedCurrentSelectedProduct.googleDiscountExpirationDate;
                delete clonedCurrentSelectedProduct.googleDiscountPrice;
                delete clonedCurrentSelectedProduct.googleDiscountTimeStamp;
            }

            if (currentSelectedProduct.specialPrice) {
                delete clonedCurrentSelectedProduct.specialPriceExpirationDate;
                delete clonedCurrentSelectedProduct.specialPrice;
                delete clonedCurrentSelectedProduct.specialPriceTimeStamp;
            }

            this.setState({ selectedProduct: clonedCurrentSelectedProduct, variants: cleanupVariants });
        }, discountRemainingTime * 1000);
    }

    getSelectedProductData(selectedVariant, product, variants, automatedDiscountData) {
        if (!inServer && variants.length && selectedVariant.part_no) {
            const search = this.props.page.search;
            const query = getQueryParams({ search });
            let queryParams = "";

            for (const key in query) {
                if (query.hasOwnProperty(key) && key !== "p") {
                    queryParams += `&${key}=${query[key]}`;
                }
            }

            const location = window.location;
            const currentPathAndSearch = `${location.pathname}${location.search}`;
            const newPathAndSearch = `${location.pathname}?p=${selectedVariant.part_no}${queryParams}`;
            if (currentPathAndSearch !== newPathAndSearch) {
                window.history.replaceState(null, null, newPathAndSearch);
            }
        } else if (!inServer) {
            const search = this.props.page.search;
            const query = getQueryParams({ search });
            const location = window.location;
            let queryParams = "";
            if (!isEmpty(query)) {
                if (query.hasOwnProperty("p")) {
                    delete query["p"];
                }
                if (!isEmpty(query)) {
                    queryParams = `?${buildQueryString(query)}`;
                }
            }
            const currentPathAndSearch = `${location.pathname}${location.search}`;
            const newPathAndSearch = `${location.pathname}${queryParams}`;
            if (currentPathAndSearch !== newPathAndSearch) {
                window.history.replaceState(null, null, newPathAndSearch);
            }
        }

        const selectedProduct = Object.keys(selectedVariant || {}).length ? selectedVariant : product;
        const isPackage = isPackageProduct(selectedProduct);
        const isVariant = isVariantProduct(selectedProduct);
        const productImages = product.files || [];
        const variantImages = isVariant ? selectedVariant.files || [] : [];

        const packageImages = isPackage
            ? (product.included_products || []).reduce((images, incProduct) => {
                  if (incProduct.format?.image) {
                      return [
                          ...images,
                          {
                              name: incProduct.name,
                              format: incProduct.format,
                              type: 1,
                          },
                      ];
                  }
                  return images;
              }, [])
            : [];
        const files = [...productImages, ...variantImages, ...packageImages];

        const selectedProductParametrics = !isPackage
            ? selectedProduct.parametrics
            : (selectedProduct.included_products || [])
                  .map((p) => ({ name: p.name, parametrics: p.parametrics }))
                  .filter((p) => p.parametrics.length);
        const parametrics = JSON.parse(JSON.stringify(selectedProductParametrics || []));

        product.parametrics.forEach(
            (parametric) =>
                parametrics.every((addedParametric) => addedParametric.code !== parametric.code) &&
                parametrics.push(parametric),
        );
        const variantParametrics = !isPackage
            ? selectedProduct.variant_parametrics
            : (selectedProduct.included_products || [])
                  .map((p) => ({ name: p.name, variant_parametrics: p.variant_parametrics }))
                  .filter((p) => p.variant_parametrics.length);

        const matchingVariant = variants.find((variant) => variant.part_no === selectedProduct.part_no);

        return {
            categories: selectedVariant.categories || product.categories || [],
            description: selectedVariant.description || product.description,
            eanCode: selectedVariant.ean_code || product.ean_code,
            files,
            flags: selectedVariant?.format?.flags || product?.format?.flags || [],
            format: selectedVariant.format || product.format || {},
            id: selectedVariant.id,
            manufacturer: selectedVariant.manufacturer || product.manufacturer,
            name: selectedVariant.variant_name || product.name,
            onHand: selectedVariant.on_hand || product.on_hand,
            onHandStore: selectedVariant.on_hand_store || product.on_hand_store,
            parametrics,
            partNo: selectedProduct.part_no,
            manufacturerPartNo: selectedProduct.manufacturer?.part_no || product.manufacturer?.part_no,
            priceExVat: selectedProduct.price || product.price,
            standardPriceExVat: selectedProduct.price_standard || product.price_standard,
            recommendedPriceExVat: selectedProduct.price_recommended || product.price_recommended,

            // GOOGLE AUTOMATED DISCOUNTS
            googleDiscountExpirationDate:
                automatedDiscountData?.googleDiscountExpirationDate ||
                matchingVariant?.googleDiscountExpirationDate ||
                selectedProduct.googleDiscountExpirationDate,
            googleDiscountPrice:
                automatedDiscountData?.googleDiscountPrice ||
                matchingVariant?.googleDiscountPrice ||
                selectedProduct.googleDiscountPrice,
            googleDiscountTimeStamp:
                automatedDiscountData?.googleDiscountTimeStamp ||
                matchingVariant?.googleDiscountTimeStamp ||
                selectedProduct.googleDiscountTimeStamp,

            // SPECIAL PRICE
            specialPriceExpirationDate:
                automatedDiscountData?.specialPriceExpirationDate ||
                matchingVariant?.specialPriceExpirationDate ||
                selectedProduct.specialPriceExpirationDate,
            specialPrice:
                automatedDiscountData?.specialPrice || matchingVariant?.specialPrice || selectedProduct.specialPrice,
            specialPriceTimeStamp:
                automatedDiscountData?.specialPriceTimeStamp ||
                matchingVariant?.specialPriceTimeStamp ||
                selectedProduct.specialPriceTimeStamp,

            catalogPriceExVat: selectedProduct.price_catalog || product.price_catalog,
            vatRate: selectedProduct.vat_rate || product.vat_rate,
            type: selectedProduct.type,
            quantity: selectedProduct.quantity,
            included_products: selectedProduct.included_products,
            price_list_id: selectedProduct.price_list_id,
            price_list_code: selectedProduct.price_list_code,
            isPackage,
            variant_parametrics: variantParametrics,
        };
    }

    getProductRelations = async () => {
        const productId = this.state.product && this.state.product.id;

        if (!productId || !this.props.shouldFetchRelations) {
            this.setState({ relationsFetched: true });
            return;
        }

        try {
            const response = await GetProductRelations(productId, { as_variants: true });

            const relationsByCode = (response?.data || []).reduce((acc, group) => {
                if (group?.code) {
                    acc[group.code] = group.relations?.items || [];
                }
                return acc;
            }, {});

            const productPageRelations = response?.data.find(({ code }) => code === PRODUCT_PAGE_RELATIONS);
            const productRelations = productPageRelations?.relations?.items || [];
            const variantRelations = productPageRelations?.variant_relations || [];

            const mappedVariantRelations = variantRelations.reduce((allRelations, relation) => {
                const relations = relation?.relations?.items || [];

                const mappedVariants = relations.reduce((products, product) => {
                    let firstParentMatch = products.length
                        ? products.find((p) => p.oldVariants.some((v) => v.id === product.id))
                        : null;
                    const variant = product.variants.find((v) => v.id === product.id);

                    if (firstParentMatch) {
                        products = products.map((p) => {
                            if (p.id === firstParentMatch.id) {
                                p.variants.push(variant);
                            }

                            return p;
                        });
                    } else {
                        firstParentMatch = product;
                        firstParentMatch.oldVariants = firstParentMatch.variants;
                        firstParentMatch.variants = [variant];

                        products.push(firstParentMatch);
                    }

                    return products;
                }, []);

                if (relation.id) {
                    allRelations[relation.id] = mappedVariants;
                }

                return allRelations;
            }, {});

            const nextState = { relationsByCode, relationsFetched: true };
            if (productRelations?.length || !isEmpty(mappedVariantRelations)) {
                nextState.productRelations = productRelations;
                nextState.variantRelations = mappedVariantRelations;
            }
            this.setState(nextState);
        } catch (e) {
            console.error(e);
            this.setState({ relationsFetched: true });
        }
    };

    returnLowestPrice = (currentPrice, newPrice) =>
        !currentPrice || currentPrice > newPrice ? newPrice : currentPrice;

    getPricingOfProducts = (products) => {
        const { product: mainProduct, selectedProductVariants, quantity = 1 } = this.state;

        let lowestMainProductPrice;
        if (mainProduct.variants?.length > 1) {
            lowestMainProductPrice = mainProduct.variants.reduce(
                (minPrice, variant) => {
                    const priceExVat = this.returnLowestPrice(minPrice.priceExVat, variant.price || 0);
                    const variantHasLowestPrice = minPrice.priceExVat === null || minPrice.priceExVat > priceExVat;
                    let standardPriceExVat = minPrice.standardPriceExVat;
                    let priceListId = minPrice.priceListId;
                    if (variantHasLowestPrice) {
                        standardPriceExVat = variant.price_standard;
                        priceListId = variant.price_list_id;
                    }
                    return {
                        priceListId,
                        priceExVat,
                        standardPriceExVat,
                    };
                },
                {
                    priceListId: mainProduct.price_list_id,
                    priceExVat: null,
                    standardPriceExVat: null,
                },
            );
        }
        const price = products.reduce(
            (price, product) => {
                if (product) {
                    // We place a fallback to quantity "1" if it does not exist.
                    const productQuantity = product.quantity || 1;
                    const { included_products: includedProducts } = product;
                    let priceListId = product.price_list_id;
                    let productPrice = product.priceExVat || product.price || 0;
                    let productStandardPriceExVat =
                        comparePriceToParent(includedProducts) ||
                        product.standardPriceExVat ||
                        product.price_standard ||
                        0;
                    const productVatRate = product.vatRate || product.vat_rate;

                    if (
                        (mainProduct.variants || []).some(({ part_no: partNo }) => partNo === product.partNo) &&
                        !selectedProductVariants[mainProduct.id]
                    ) {
                        priceListId = lowestMainProductPrice.priceListId;
                        productPrice = lowestMainProductPrice.priceExVat;
                        productStandardPriceExVat = lowestMainProductPrice.standardPriceExVat;
                    }

                    return {
                        ...price,
                        priceListId,
                        priceExVat: price.priceExVat + productPrice * productQuantity,
                        priceIncVat: price.priceIncVat + productPrice * productVatRate * productQuantity,
                        standardPriceExVat: price.standardPriceExVat + productStandardPriceExVat * productQuantity,
                    };
                }

                return price;
            },
            {
                priceListId: null,
                priceIncVat: 0,
                priceExVat: 0,
                standardPriceExVat: 0,
                quantityOfPackage: quantity > 0 ? quantity : 1,
            },
        );

        price.vatRate = price.priceIncVat / price.priceExVat || mainProduct?.vat_rate || 1;

        return price;
    };

    getStockStatus = () => {
        const { isPackage, product, selectedProductVariants } = this.state;

        const mainProduct = selectedProductVariants[product.id] || product;
        const mainProductStock =
            onHandCheck(mainProduct["on_hand"]?.value) || onHandCheck(mainProduct["on_hand_store"]?.value);

        if (isPackage) {
            let allIncludedProductsHaveStock = true;

            product.included_products.forEach((incProduct) => {
                const selectedPackageVariant = selectedProductVariants[incProduct.id] || incProduct;
                const onHandSum =
                    onHandCheck(selectedPackageVariant["on_hand"]?.value) +
                    onHandCheck(selectedPackageVariant["on_hand_store"]?.value);
                const incProductStock = Math.floor(onHandSum / selectedPackageVariant.quantity);
                if (incProductStock < 1) {
                    allIncludedProductsHaveStock = false;
                }
            });

            return allIncludedProductsHaveStock;
        }
        return mainProductStock > 0;
    };

    getQtyBreaks = async () => {
        const { product } = this.state;

        if (product && product.id) {
            try {
                const response = await GetProductPriceQtyBreak(product.id);
                if (response.data && response.data.length > 1) {
                    const filteredResponse = response.data.filter(
                        (qtyProduct) => qtyProduct.part_no === product.part_no && qtyProduct.qty_break > 1,
                    );

                    this.setState({
                        qtyBreaks: filteredResponse,
                    });
                }
            } catch (error) {
                console.error(error);
            }
        }
    };

    // ---------- CALLBACKS

    setQuantity = (value) => {
        let quantity = value;

        if (typeof quantity === "string") {
            quantity = +value;
        } else if (isNaN(quantity) || quantity === null || quantity === undefined) {
            quantity = 1;
        }

        this.setState({ quantity });
    };

    setVariant = (pId, pVariant) => {
        // Get the current selectedProductVariants from the state (as a new object)
        const selectedVariants = { ...this.state.selectedProductVariants };

        // If the productId is not the same as the new productVariant we update the state
        if (selectedVariants[pId] !== pVariant) {
            if (pVariant === undefined) {
                delete selectedVariants[pId];
            } else {
                selectedVariants[pId] = pVariant;
            }

            const newState = { selectedProductVariants: selectedVariants };

            // if new productVariant is set, update selectedProduct
            if (this.state.product && pId === this.state.product?.id) {
                newState["selectedProduct"] = this.getSelectedProductData(
                    pVariant || {},
                    this.state.product,
                    this.state.variants,
                    this.props.page.data?.automatedDiscountData,
                );
            }

            this.setState(newState);
        }
    };

    render() {
        const {
            bestHistoricalPrice,
            isPackage,
            product,
            qtyBreaks,
            quantity,
            selectedProduct,
            selectedProductVariants,
            productRelations,
            variantRelations,
            relationsByCode,
            relationsFetched,
            variants,
        } = this.state;

        let relations = productRelations;
        if (selectedProduct && variantRelations[selectedProduct?.id]) {
            relations = relations.concat(variantRelations[selectedProduct.id]);
        }

        const { loading: Loading, page, render: Render } = this.props;
        const isQuantityPackage = isVariantProduct(product || {}) && selectedProduct.isPackage;

        if (!product && Loading) {
            return <Loading />;
        }

        let totalPricing = {};

        if (isPackage) {
            totalPricing = this.getPricingOfProducts([selectedProduct]);
        } else if (!selectedProductVariants[product.id]) {
            totalPricing = this.getPricingOfProducts([selectedProduct]);
        } else {
            totalPricing = this.getPricingOfProducts([...Object.values(selectedProductVariants)]);
        }

        const isInStock = !!this.getStockStatus();

        return (
            <>
                <Meta
                    layout="product"
                    isInStock={isInStock}
                    product={product}
                    type={page.type}
                    selectedVariant={selectedProductVariants[product.id]}
                />
                <Render
                    bestHistoricalPrice={bestHistoricalPrice}
                    isExpired={!isInStock && selectedProduct.isExpired}
                    isInStock={isInStock}
                    isVariant={isVariantProduct(product)}
                    pageData={{
                        localeAlternates: page.localeAlternates,
                        defaultName: page.data?.default_name,
                        defaultUniqueUrlName: page.data?.default_unique_url_name,
                        id: page.data?.id,
                        partNo: page.data?.part_no,
                        variantPartNos: page.data?.variant_part_nos,
                    }}
                    product={product}
                    qtyBreaks={qtyBreaks}
                    quantity={quantity}
                    isQuantityPackage={isQuantityPackage}
                    selectedProduct={selectedProduct}
                    selectedProductVariants={selectedProductVariants}
                    variants={variants?.length ? variants : product.variants}
                    totalPricing={totalPricing}
                    relations={relations}
                    relationsByCode={relationsByCode}
                    relationsFetched={relationsFetched}
                    onQuantityChange={this.setQuantity}
                    onUpsellChange={this.setUpsells}
                    onVariantChange={this.setVariant}
                />
            </>
        );
    }
}

export default withRouter(injectModels(["page", "application"])(ProductPageContainer));
